import React from 'react'
import { Text } from '@lightspeed/design-system-react'
import { vn, add } from 'vend-number'

import { T } from '@transifex/react'
import { RoundedNumber } from '../../CashPaymentModal'
import { useFormatCurrencyValue } from '../../../../sale/summary-panel/useFormatCurrencyValue'

interface HeaderProps {
  amountToPay: RoundedNumber
  hasCashDiscount?: boolean
}

const RoundedMessage: React.FC<HeaderProps> = ({ amountToPay }) => {
  const formatCurrencyValue = useFormatCurrencyValue()
  const roundedAmountToPay = vn(amountToPay.rounded)
  // Format remainder to avoid BigNumber precision errors (more than 15 significant digits)
  const formattedRemainder = vn(amountToPay.remainder).toFixed(2)
  const originalAmountToPay = add(formattedRemainder, roundedAmountToPay)

  if (roundedAmountToPay.gt(originalAmountToPay)) {
    return (
      <T
        _str="Rounded up from {originalAmount}"
        originalAmount={formatCurrencyValue(originalAmountToPay.toString())}
      />
    )
  }
  return (
    <T
      _str="Rounded down from {originalAmount}"
      originalAmount={formatCurrencyValue(originalAmountToPay.toString())}
    />
  )
}

// Component
export const Header: React.FC<HeaderProps> = ({
  amountToPay,
  hasCashDiscount,
}) => {
  const formatCurrencyValue = useFormatCurrencyValue()
  const roundedAmountToPay = vn(amountToPay.rounded)
  // Check if there's a meaningful rounding difference — ignore sub-cent
  // precision noise that can arise from the LSP tax adjustment division.
  const hasRoundingDifference = vn(amountToPay.remainder).abs().gt(0.005)

  const balanceClassName = hasCashDiscount
    ? 'payment-cash-modal-header-balance payment-cash-modal-header-balance--discounted'
    : 'payment-cash-modal-header-balance'

  return (
    <div className="payment-cash-modal-header">
      <div className="payment-cash-modal-header-headline">
        <T _str="Amount to pay" />
      </div>
      <div className={balanceClassName}>
        {formatCurrencyValue(roundedAmountToPay.toString())}
        <Text tag="p">
          {hasRoundingDifference && (
            <RoundedMessage amountToPay={amountToPay} />
          )}
        </Text>
      </div>
    </div>
  )
}
